import React, {
  Fragment,
  ReactNode,
  memo,
  useEffect,
  useLayoutEffect,
  useState,
} from 'react';
import { Menu, Transition } from '@headlessui/react';
import { SVGIcon } from '~/components/SVGIcons';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'next/router';

export type Languages = 'pt' | 'en' | 'es';

export type Options = {
  id: string;
  name: Languages;
  target: ReactNode;
};

interface IDropdown {
  title?: string;
  disabled?: boolean;
  options: Options[];
  currentOption?: string;
  currentSelectedOption?: Options | null;
  className?: string;
  handleSelect?: (param: string) => void;
  getData?: () => void;
  onChange?: (option: Options | null) => void;
}

function classNames(...classes: any) {
  return classes.filter(Boolean).join(' ');
}

const LanguageSelector: React.FC<IDropdown> = ({
  title,
  options,
  disabled,
  currentOption,
  currentSelectedOption = null,
  getData,
  onChange,
  handleSelect,
  className,
}) => {
  const router = useRouter();
  const [selectedOption, setSelectedOption] = useState<Options | null>(null);

  const handleOptionSelect = (option: Options) => {
    const newRoute = `/${option.name}`;
    if (!options?.length && getData) getData();
    if (handleSelect) handleSelect(option.id);
    setSelectedOption(option);
    router.push(newRoute);
    if (onChange) {
      onChange(option);
    }
  };

  useEffect(() => {
    if (currentSelectedOption !== null) {
      handleOptionSelect(currentSelectedOption);
    } else {
      setSelectedOption(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={`w-[40px] -translate-x-4 flex-col ${className}`}>
      <h1 className="pb-2 text-sm text-black">{title}</h1>
      <Menu as="div" className="relative inline-block w-[40px] text-left">
        <div className="flex w-[40px]">
          <Menu.Button
            disabled={disabled}
            className={`inline-flex items-center justify-between bg-transparent
            ${disabled && 'opacity-30'} 
               `}
          >
            {selectedOption ? selectedOption.target : ''}
            <SVGIcon
              name="expand"
              color="#45433A"
              size="small"
              className="ml-1 mt-4"
            />
          </Menu.Button>
        </div>

        <Transition
          as={Fragment}
          enter="transition ease-out duration-100"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95"
        >
          <Menu.Items
            className={`absolute z-30 mt-2 w-full origin-top-right rounded-md bg-white shadow-lg focus:outline-none`}
          >
            <div className="py-2">
              {options?.map(option => (
                <Menu.Item key={option.id}>
                  {({ active }) => (
                    <button
                      type="button"
                      onClick={() => handleOptionSelect(option)}
                      className={classNames(
                        active
                          ? 'bg-gray-100 text-gray-900'
                          : 'text-neutra3-cinzaquente40',
                        `block w-full px-2 py-1 text-left text-sm`,
                      )}
                    >
                      {currentOption ?? option.target}
                    </button>
                  )}
                </Menu.Item>
              ))}
            </div>
          </Menu.Items>
        </Transition>
      </Menu>
    </div>
  );
};

export default LanguageSelector;
